import {
  ProfileUpdateInputSchema,
  ProfileUpdateResponseSchema,
  type ProfileUpdateInput,
  type ProfileUpdateResponse,
} from '../contracts/profile';
import type { RequestFn } from './client';

/**
 * Namespace `client.profile` — транспорт для `PUT /api/profile` (Блок 11,
 * экран 2.11.2 «Редактирование профиля», ADR-0091). **ТОЛЬКО транспорт.**
 *
 * **Out of scope (явно):**
 *   - Реальный эндпоинт `PUT /api/profile` + персист в региональный Postgres
 *     (P-007) + серверная проверка `CITY_REGION_MAP[city] === user.region`
 *     (P-008) — Region API (Фаза 1). 11.B — MSW-мок.
 *   - Смена телефона — отдельный OTP-флоу (контракт с auth-бэком, задача 1.2),
 *     НЕ в этом namespace (§1a, P-010). 11.B мокает под-флоу в UI.
 *
 * `auth: true` — приватный эндпоинт, пользователь берётся из JWT (не из тела).
 * Относительный путь (P-013), типизированный клиент (P-012).
 *
 * P-009 защита уровня 2: client-side `safeParse` ДО сети — невалидный payload
 * (пустое имя, город вне CITIES, неверная specialization) отклоняется без
 * сетевого вызова. Симметрия с `cooperation.apply()` / `feedback.create()`.
 */
export interface ProfileApi {
  /**
   * PUT /api/profile — обновить профиль (имя/специализация/город).
   *
   * @param input — `ProfileUpdateInput` (Zod-валидирован client-side через safeParse).
   * @returns `ProfileUpdateResponse` — эхо `{ name, specialization, city }`.
   * @throws `z.ZodError` если client-side валидация не прошла (нет сетевого вызова).
   * @throws `ApiClientError` при 400/401/409 / network / 5xx / PARSE_ERROR.
   */
  update(input: ProfileUpdateInput): Promise<ProfileUpdateResponse>;
}

export function createProfileApi(request: RequestFn): ProfileApi {
  return {
    update: (input) => {
      const parsed = ProfileUpdateInputSchema.safeParse(input);
      if (!parsed.success) {
        return Promise.reject(parsed.error);
      }
      return request({
        method: 'PUT',
        path: '/api/profile',
        body: parsed.data,
        responseSchema: ProfileUpdateResponseSchema,
        auth: true,
      });
    },
  };
}
